import PropTypes from "prop-types";
import React, { useMemo, useState } from "react";

import { getBootstrapData } from "@swa-ui/bootstrap";
import { SMALL, useDeviceInfo } from "@swa-ui/browser";
import { MultiselectFormField } from "@swa-ui/form";
import i18n from "@swa-ui/locale";

import { ALL_OTHERS, ALL_RAPID_REWARDS, NO_PREFERENCE } from "../../../defines/vendorGroups";
import { carVendorsData, useCarVendorData } from "../carVendorData";

export const RentalCarFormField = (props) => {
  const { className, disabled, rentalCompany, setValue, setRentalVehicleInfo } = props;
  const { screenSize } = useDeviceInfo();
  const [allOtherVendors, setAllOtherVendors] = useState(false);
  const [allRRVendors, setAllRRVendors] = useState(false);
  const [noPreferenceSet, setNoPreferenceSet] = useState(true);
  const carVendorData = getBootstrapData("car/car-vendor-data");
  const { getNonRapidRewardsVendorIds, getRapidRewardsVendorIds } = useCarVendorData();
  const allOtherVendorsList = useMemo(
    () => getNonRapidRewardsVendorIds(carVendorData),
    [carVendorData]
  );
  const allRRVendorsList = useMemo(() => getRapidRewardsVendorIds(carVendorData), [carVendorData]);
  const allOthersCount = useMemo(() => allOtherVendorsList.length, [allOtherVendorsList]);
  const allRRVendorsCount = useMemo(() => allRRVendorsList.length, [allRRVendorsList]);

  return <MultiselectFormField {...getRentalCompanyProps()} />;

  function getRentalCompanyProps() {
    return {
      className,
      componentProps: {
        fullScreen: screenSize === SMALL,
        getSelectionValueText: getRentalCompanyInputText,
        list: carVendorsData(carVendorData),
        minimumListWidth: "full-width",
        onChange: handleRentalCompanyChange,
        value: rentalCompany,
      },
      disabled,
      label: i18n("CarBookingForm__RENTAL_COMPANY"),
      name: "rentalCompany",
    };
  }

  function handleRentalCompanyChange(event) {
    const { value: newValue } = event.target;
    const normalizationSteps = [
      duplicateSelection,
      normalizeNoPreference,
      normalizeAllRapidRewards,
      normalizeAllOthers,
      prependGroupLabels,
    ];
    let result = newValue;

    result = normalizationSteps.reduce(
      (accumulatedValues, normalizationStep) => normalizationStep(accumulatedValues, newValue),
      newValue
    );

    if (result.length === 0) {
      result = [NO_PREFERENCE];
    }

    setNoPreferenceSet(result.indexOf(NO_PREFERENCE) !== -1);
    setAllRRVendors(result.indexOf(ALL_RAPID_REWARDS) !== -1);
    setAllOtherVendors(result.indexOf(ALL_OTHERS) !== -1);
    setValue("rentalCompany", result);
    setRentalVehicleInfo({ rentalCompany: result });
  }

  function normalizeNoPreference(result, newValue) {
    let normalized;

    if (isNoPreferenceAdded(newValue)) {
      normalized = [NO_PREFERENCE];
    } else {
      normalized = result.filter((selection) => selection !== NO_PREFERENCE);
    }

    return normalized;
  }

  function normalizeAllRapidRewards(result, newValue) {
    let normalizedResult = result;

    if (isAllRapidRewardOptionAdded(newValue)) {
      const filtered = newValue.filter((value) => value !== NO_PREFERENCE && value !== ALL_OTHERS);

      normalizedResult = addAllRapidRewardOptions(removeAllRapidRewardOption(filtered));
    } else if (isAllRapidRewardOptionRemoved(newValue)) {
      normalizedResult = removeAllRapidRewardOption(newValue);
    }

    return normalizedResult;
  }

  function normalizeAllOthers(result, newValue) {
    if (isAllOthersAdded(newValue)) {
      result = addOtherOptions(
        removeAllOtherOptions(
          removeAllOptions(
            newValue.filter((value) => value !== NO_PREFERENCE && value !== ALL_OTHERS),
            allOtherVendorsList
          )
        )
      );
    } else if (isAllOthersRemoved(newValue)) {
      result = removeAllOtherOptions(newValue);
    }

    return result;
  }

  function duplicateSelection(result) {
    const allOthersSelected = areAllOthersOptionsSelected(result);
    const allRapidRewardsSelected = areAllRapidRewardsSelected(result);
    let final = result;

    if (!allRapidRewardsSelected && result.indexOf(ALL_RAPID_REWARDS) !== -1) {
      final = final.filter((selection) => selection !== ALL_RAPID_REWARDS);
    }

    if (!allOthersSelected && result.indexOf(ALL_OTHERS) !== -1) {
      final = final.filter((selection) => selection !== ALL_OTHERS);
    }

    return final;
  }

  function prependGroupLabels(result) {
    const allOthersSelected = areAllOthersOptionsSelected(result);
    const allRapidRewardsSelected = areAllRapidRewardsSelected(result);
    let final = [...result];

    if (allRapidRewardsSelected && result.indexOf(ALL_RAPID_REWARDS) === -1) {
      final = [ALL_RAPID_REWARDS, ...final];
    }

    if (allOthersSelected && result.indexOf(ALL_OTHERS) === -1) {
      final = [ALL_OTHERS, ...final];
    }

    return final;
  }

  function isNoPreferenceAdded(newValue) {
    return newValue.indexOf(NO_PREFERENCE) !== -1 && !noPreferenceSet;
  }

  function isAllRapidRewardOptionAdded(newValue) {
    return newValue.indexOf(ALL_RAPID_REWARDS) !== -1 && !allRRVendors;
  }

  function isAllRapidRewardOptionRemoved(newValue) {
    return newValue.indexOf(ALL_RAPID_REWARDS) === -1 && allRRVendors;
  }

  function isAllOthersAdded(newValue) {
    return newValue.indexOf(ALL_OTHERS) !== -1 && !allOtherVendors;
  }

  function isAllOthersRemoved(newValue) {
    return newValue.indexOf(ALL_OTHERS) === -1 && allOtherVendors;
  }

  function removeAllRapidRewardOption(newValue) {
    return removeAllOptions(newValue, allRRVendorsList);
  }

  function removeAllOtherOptions(newValue) {
    return removeAllOptions(newValue, allOtherVendorsList);
  }

  function removeAllOptions(newValue, options) {
    let workingValue = newValue;

    options.forEach((optionToRemove) => {
      workingValue = workingValue.filter((option) => option !== optionToRemove);
    });

    return workingValue;
  }

  function addAllRapidRewardOptions(newValue) {
    return newValue.concat(allRRVendorsList);
  }

  function addOtherOptions(newValue) {
    return newValue.concat(allOtherVendorsList);
  }

  function areAllRapidRewardsSelected(values) {
    return getSelectedRapidRewardsCount(values) === allRRVendorsCount;
  }

  function areAllOthersOptionsSelected(values) {
    return getSelectedOthersCount(values) === allOthersCount;
  }

  function getSelectedRapidRewardsCount(values) {
    return getSelectedCount(values, allRRVendorsList);
  }

  function getSelectedOthersCount(values) {
    return getSelectedCount(values, allOtherVendorsList);
  }

  function getRentalCompanyInputText(values) {
    const selectedOthersCount = getSelectedOthersCount(values);
    const selectedRRCount = getSelectedRapidRewardsCount(values);
    let text;

    if (values.indexOf(NO_PREFERENCE) !== -1 || values === "") {
      text = `${i18n("CarBookingForm__TEXT_ALL")}${values !== NO_PREFERENCE ? " " : ""}`;
    } else {
      text = `${selectedOthersCount + selectedRRCount} selected`;
    }

    return text;
  }

  function getSelectedCount(values, options) {
    return options.reduce(
      (accumulator, option) => accumulator + (values.indexOf(option) !== -1 ? 1 : 0),
      0
    );
  }
};

RentalCarFormField.propTypes = {
  /** Additional class to position the component.  */
  className: PropTypes.string,

  /** Boolean to determine if rentalCompany dropdown should be disabled.  */
  disabled: PropTypes.bool,

  /** Array of items selected from the dropdown list. */
  rentalCompany: PropTypes.arrayOf(PropTypes.string),

  /** Function to set the value of form field labeled rentalCompany. */
  setValue: PropTypes.func,

  /** Function to update the rental information upon item selection. */
  setRentalVehicleInfo: PropTypes.func,
};
